import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Search, User, Settings as SettingsIcon, LogOut, Menu, X, ShieldCheck, BadgeCheck, Copy, TrendingUp, Radio, Bell, History, HeadphonesIcon, Coins, RefreshCw, ArrowLeftRight, Shuffle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BicoinLogo } from '../BicoinLogo'
import { useAuth } from '../../context/AuthContext'
import { getBalance } from '../../services/walletService'
import { getProfile } from '../../services/userService'
import { useToast } from '../../context/ToastContext'

interface HeaderProps {
  activePage: string
  hideMobile?: boolean
}

function Header({ activePage, hideMobile = false }: HeaderProps) {
  const navigate = useNavigate()
  const { isLoggedIn, logout, user, refreshUser } = useAuth()
  const { toast } = useToast()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isEarnDropdownOpen, setIsEarnDropdownOpen] = useState(false)
  const [isWalletDropdownOpen, setIsWalletDropdownOpen] = useState(false)
  const [isTradeDropdownOpen, setIsTradeDropdownOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [balance, setBalance] = useState<number | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const earnDropdownRef = useRef<HTMLDivElement>(null)
  const walletDropdownRef = useRef<HTMLDivElement>(null)
  const tradeDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false)
      }
      if (earnDropdownRef.current && !earnDropdownRef.current.contains(event.target as Node)) {
        setIsEarnDropdownOpen(false)
      }
      if (walletDropdownRef.current && !walletDropdownRef.current.contains(event.target as Node)) {
        setIsWalletDropdownOpen(false)
      }
      if (tradeDropdownRef.current && !tradeDropdownRef.current.contains(event.target as Node)) {
        setIsTradeDropdownOpen(false)
      }
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      const fetchBalance = async () => {
        try {
          const data = await getBalance()
          setBalance(data.balance)
        } catch (error) {
          console.error('Header balance fetch error:', error)
        }
      }
      fetchBalance()
      const interval = setInterval(fetchBalance, 30000)
      return () => clearInterval(interval)
    }
  }, [isLoggedIn])

  // Always refresh profile from API on login to ensure uid/kycStatus/avatar are current
  useEffect(() => {
    if (!isLoggedIn) return
    getProfile().then((p: any) => {
      if (!p) return
      const stored = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const updated = {
        ...stored,
        uid: p.uid,
        kycStatus: p.kycStatus,
        avatar: p.avatar,
        name: p.name || stored.name,
        isGoogleAuthenticatorEnabled: p.isGoogleAuthenticatorEnabled,
        hasFundPassword: p.hasFundPassword,
      }
      localStorage.setItem('userInfo', JSON.stringify(updated))
      refreshUser()
    }).catch(() => {})
  }, [isLoggedIn])

  const handleLogout = () => {
    logout()
    setIsProfileOpen(false)
    setIsMobileMenuOpen(false)
    toast('Logged out successfully', 'success')
    navigate('/')
  }

  const userName = user?.name?.trim() || 'User'
  const userEmail = user?.email?.trim() || 'No email available'
  const userId = (user as any)?.uid ? `UID: ${(user as any).uid}` : user?._id ? `UID: ${user._id.slice(0, 8).toUpperCase()}` : 'UID: --------'
  const userInitial = (userName || userEmail || 'B').charAt(0).toUpperCase()
  const userAvatar = (user as any)?.avatar || null
  const isKycVerified = (user as any)?.kycStatus === 'verified'

  const searchItems = [
    { label: 'Home',            path: '/dashboard/home' },
    { label: 'Market',          path: '/dashboard/market' },
    { label: 'Spot Trade',      path: '/dashboard/trade' },
    { label: 'Copy Trading',    path: '/dashboard/signals' },
    { label: 'Futures',         path: '/dashboard/futures' },
    { label: 'Deposit',         path: '/dashboard/deposit' },
    { label: 'Withdraw',        path: '/dashboard/withdraw' },
    { label: 'Transfer',        path: '/dashboard/transfer' },
    { label: 'Assets',          path: '/dashboard/assets' },
    { label: 'Transfer',        path: '/dashboard/transfer' },
    { label: 'Convert',         path: '/dashboard/convert' },
    { label: 'Transaction History', path: '/dashboard/history' },
    { label: 'Earn / Staking',  path: '/dashboard/staking' },
    { label: 'Academy',         path: '/dashboard/academy' },
    { label: 'Campaign',        path: '/dashboard/campaign' },
    { label: 'Profile',         path: '/dashboard/profile' },
    { label: 'Settings',        path: '/dashboard/settings' },
    { label: 'Support',         path: '/dashboard/support' },
    { label: 'KYC Verification', path: '/dashboard/kyc' },
    { label: 'Referral',        path: '/dashboard/referral' },
  ]

  const filteredSearch = searchQuery.trim().length > 0
    ? searchItems.filter(i => i.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : searchItems

  return (
    <header className={`header ${hideMobile ? 'hide-on-mobile' : ''}`}>
      <div className="header-left">
        <div className="logo" onClick={() => navigate('/dashboard/home')} style={{ cursor: 'pointer', height: '100%', display: 'flex', alignItems: 'center' }}>
          <BicoinLogo height={36} className="logo-img" />
        </div>

        <nav className="nav-menu desktop-only">
          {/* Wallet */}
          <div className="nav-item-dropdown-container" ref={walletDropdownRef}>
            <div className={`nav-item dropdown ${['deposit','withdraw','transfer'].includes(activePage) ? 'active' : ''}`} onClick={() => { setIsWalletDropdownOpen(!isWalletDropdownOpen); setIsTradeDropdownOpen(false); setIsEarnDropdownOpen(false); }} style={{ cursor: 'pointer' }}>
              <span>Wallet</span>
              <ChevronDown size={16} style={{ transform: isWalletDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {isWalletDropdownOpen && (
              <div className="nav-dropdown-menu">
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/deposit'); setIsWalletDropdownOpen(false); }}>Deposit</div>
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/withdraw'); setIsWalletDropdownOpen(false); }}>Withdraw</div>
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/transfer'); setIsWalletDropdownOpen(false); }}>Transfer</div>
              </div>
            )}
          </div>

          {/* Market */}
          <div className={`nav-item dropdown ${activePage === 'market' ? 'active' : ''}`} onClick={() => navigate('/dashboard/market?view=list&tab=spot')} style={{ cursor: 'pointer' }}>
            <span>Market</span>
          </div>

          {/* Trade */}
          <div className="nav-item-dropdown-container" ref={tradeDropdownRef}>
            <div className={`nav-item dropdown ${['trade','signals'].includes(activePage) ? 'active' : ''}`} onClick={() => { setIsTradeDropdownOpen(prev => !prev); setIsWalletDropdownOpen(false); setIsEarnDropdownOpen(false); }} style={{ cursor: 'pointer' }}>
              <span>Trade</span>
              <ChevronDown size={16} style={{ transform: isTradeDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {isTradeDropdownOpen && (
              <div className="nav-dropdown-menu">
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/trade'); setIsTradeDropdownOpen(false); }}><TrendingUp size={18} /><span>Spot Trade</span></div>
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/signals'); setIsTradeDropdownOpen(false); }}><Radio size={18} /><span>Copy Trading</span></div>
              </div>
            )}
          </div>

          {/* Futures */}
          <div className={`nav-item dropdown ${activePage === 'futures' ? 'active' : ''}`} onClick={() => { navigate('/dashboard/futures'); setIsWalletDropdownOpen(false); setIsEarnDropdownOpen(false); setIsTradeDropdownOpen(false); }} style={{ cursor: 'pointer' }}>
            <span>Futures</span>
          </div>

          {/* More: Earn / Academy / Campaign */}
          <div className="nav-item-dropdown-container" ref={earnDropdownRef}>
            <div className={`nav-item dropdown ${['earn','staking','academy','campaign'].includes(activePage) ? 'active' : ''}`} onClick={() => { setIsEarnDropdownOpen(!isEarnDropdownOpen); setIsWalletDropdownOpen(false); setIsTradeDropdownOpen(false); }} style={{ cursor: 'pointer' }}>
              <span>More</span>
              <ChevronDown size={16} style={{ transform: isEarnDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>
            {isEarnDropdownOpen && (
              <div className="nav-dropdown-menu">
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/staking'); setIsEarnDropdownOpen(false); }}>Earn</div>
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/academy'); setIsEarnDropdownOpen(false); }}>Academy</div>
                <div className="nav-dropdown-item" onClick={() => { navigate('/dashboard/campaign'); setIsEarnDropdownOpen(false); }}>Campaign</div>
              </div>
            )}
          </div>
        </nav>
      </div>

      <div className="header-right">
        {isLoggedIn ? (
          <>
            <div className="header-balance-btn desktop-only" onClick={() => navigate('/dashboard/assets')} style={{ cursor: 'pointer' }}>
              <div className="balance-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-500">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </div>
              <span className="balance-amount">${balance !== null ? balance.toFixed(2) : '...'}</span>
              <ChevronDown size={14} />
            </div>

            <button className="header-icon-btn" onClick={() => { setShowSearch(true); setSearchQuery(''); }}>
              <Search size={20} />
            </button>

            <button className="header-icon-btn" onClick={() => navigate('/dashboard/notifications')} title="Notifications">
              <Bell size={20} />
            </button>

            <div className="profile-container" ref={dropdownRef}>
              <div
                className={`profile-btn ${activePage === 'profile' || activePage === 'settings' ? 'active' : ''}`}
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                style={{ cursor: 'pointer' }}
              >
                {userAvatar
                  ? <img src={userAvatar} alt={userName} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                  : <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ui-bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>{userInitial}</span>
                }
                <ChevronDown className="desktop-only" size={14} style={{ transform: isProfileOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginRight: 6 }} />
              </div>

              {isProfileOpen && (
                <div className="profile-dropdown-menu">
                  <div className="dropdown-user-info">
                    {userAvatar
                      ? <img src={userAvatar} alt={userName} className="user-avatar-small" style={{ objectFit: 'cover' }} />
                      : <div className="user-avatar-small">{userInitial}</div>
                    }
                    <div className="user-details">
                      <div className="user-name">{userName}</div>
                      <div className="user-email">{userEmail}</div>
                      <div
                        className="user-uid-row"
                        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(String((user as any)?.uid || '')); }}
                        title="Click to copy"
                      >
                        <span>{userId}</span>
                        <Copy size={11} />
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item" onClick={() => { navigate('/dashboard/profile'); setIsProfileOpen(false); }}>
                    <User size={18} />
                    <span>Profile</span>
                  </div>
                  <div className="dropdown-item" onClick={() => { navigate('/dashboard/settings'); setIsProfileOpen(false); }}>
                    <SettingsIcon size={18} />
                    <span>Settings</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout" onClick={handleLogout}>
                    <LogOut size={18} />
                    <span>Log Out</span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <button className="header-icon-btn">
              <Search size={20} />
            </button>
            <button className="auth-btn signin-header-btn" onClick={() => navigate('/signin')}>
              Sign In
            </button>
            <button className="auth-btn signup-header-btn desktop-only" onClick={() => navigate('/signup')}>
              Sign Up
            </button>
          </>
        )}

        <button className="header-icon-btn mobile-only" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
        <div className={`mobile-menu-drawer ${isMobileMenuOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>

          {/* Drawer top bar — logo + close */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--ui-border)', flexShrink: 0 }}>
            <BicoinLogo height={32} />
            <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--ui-text-muted)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
              <X size={22} />
            </button>
          </div>

          <div className="mobile-drawer-content">
            {isLoggedIn ? (
              <>
                {/* User card — matches desktop dropdown */}
                <div className="md-user-header">
                  <div style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div className="md-avatar-wrapper">
                        {userAvatar
                          ? <img src={userAvatar} alt={userName} className="md-avatar" style={{ objectFit: 'cover' }} />
                          : <div className="md-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1CD4A7', color: '#000', fontWeight: 700, fontSize: 18 }}>{userInitial}</div>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: 'var(--ui-text-primary)', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
                        <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userEmail}</div>
                      </div>
                    </div>
                    {/* UID row with copy */}
                    <div
                      onClick={() => { navigator.clipboard.writeText(String((user as any)?.uid || '')); toast('UID copied', 'success'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ui-bg-primary)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginBottom: 10 }}
                    >
                      <span style={{ color: 'var(--ui-text-muted)', fontSize: 12, flex: 1 }}>{userId}</span>
                      <Copy size={11} color="#71717a" />
                    </div>
                    {/* Verified badge */}
                    <div
                      onClick={() => { navigate('/dashboard/kyc'); setIsMobileMenuOpen(false); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: isKycVerified ? 'rgba(16,185,129,0.1)' : 'rgba(246,70,93,0.15)', color: isKycVerified ? '#10b981' : '#F6465D', cursor: 'pointer' }}
                    >
                      <ShieldCheck size={12} />
                      <span>{isKycVerified ? 'Verified' : 'Unverified'}</span>
                    </div>
                  </div>
                </div>

                <div className="md-divider"></div>

                {/* Navigation Menu */}
                <nav className="md-menu-list">
                  <div className="md-menu-section-title">Navigation</div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/market'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                    <span>Market</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/trade'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3v18h18"></path>
                        <path d="m19 9-5 5-4-4-3 3"></path>
                      </svg>
                    </div>
                    <span>Trade</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/futures'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                        <path d="M3 9h18"></path>
                        <path d="M9 21V9"></path>
                      </svg>
                    </div>
                    <span>Futures</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/signals'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><Radio size={20} /></div>
                    <span>Copy Trading</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/deposit'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v20"></path>
                        <path d="m17 7-5-5-5 5"></path>
                        <rect x="3" y="17" width="18" height="4" rx="1"></rect>
                      </svg>
                    </div>
                    <span>Deposit Crypto</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/withdraw'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22V2"></path>
                        <path d="m7 17 5 5 5-5"></path>
                        <rect x="3" y="3" width="18" height="4" rx="1"></rect>
                      </svg>
                    </div>
                    <span>Withdraw Crypto</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/assets'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                        <line x1="2" y1="10" x2="22" y2="10"></line>
                      </svg>
                    </div>
                    <span>Assets</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/transfer'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 3h5v5"></path>
                        <path d="M8 21H3v-5"></path>
                        <path d="M21 3L14 10"></path>
                        <path d="M3 21l7-7"></path>
                      </svg>
                    </div>
                    <span>Transfer</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/earn'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><Coins size={20} /></div>
                    <span>Earn</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/campaign'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"></path>
                        <path d="M12 2v2"></path>
                        <path d="M12 20v2"></path>
                        <path d="m4.93 4.93 1.41 1.41"></path>
                        <path d="m17.66 17.66 1.41 1.41"></path>
                        <path d="M2 12h2"></path>
                        <path d="M20 12h2"></path>
                        <path d="m6.34 17.66-1.41 1.41"></path>
                        <path d="m19.07 4.93-1.41 1.41"></path>
                      </svg>
                    </div>
                    <span>Campaign</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/academy'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                    </div>
                    <span>Academy</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/history'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><History size={20} /></div>
                    <span>History</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/support'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><HeadphonesIcon size={20} /></div>
                    <span>Support</span>
                  </div>
                </nav>

                <div className="md-divider"></div>

                {/* Account Menu */}
                <nav className="md-menu-list">
                  <div className="md-menu-section-title">Account</div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/profile'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><User size={20} /></div>
                    <span>Profile</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/kyc'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><ShieldCheck size={20} /></div>
                    <span>KYC Verification</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/transfer'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><ArrowLeftRight size={20} /></div>
                    <span>Transfer</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/convert'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><Shuffle size={20} /></div>
                    <span>Convert</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/notifications'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><Bell size={20} /></div>
                    <span>Notifications</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/settings'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box"><SettingsIcon size={20} /></div>
                    <span>Settings</span>
                  </div>
                </nav>

                <div className="md-footer mt-auto">
                  <div className="md-menu-item logout" onClick={handleLogout}>
                    <div className="md-icon-box"><LogOut size={20} /></div>
                    <span>Logout</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Guest Mobile Menu */}
                <BicoinLogo height={36} className="logo-img md-guest-logo" />
                <div className="md-guest-header">
                  <h3 className="md-guest-title">Welcome to Bicoin</h3>
                  <p className="md-guest-subtitle">Sign in to start trading</p>
                </div>

                <div className="md-auth-buttons">
                  <button className="md-auth-btn primary" onClick={() => { navigate('/signin'); setIsMobileMenuOpen(false); }}>
                    Sign In
                  </button>
                  <button className="md-auth-btn secondary" onClick={() => { navigate('/signup'); setIsMobileMenuOpen(false); }}>
                    Create Account
                  </button>
                </div>

                <div className="md-divider"></div>

                <nav className="md-menu-list">
                  <div className="md-menu-section-title">Explore</div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/home'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <span>Home</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/academy'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                    </div>
                    <span>Academy</span>
                  </div>
                  <div className="md-menu-item" onClick={() => { navigate('/dashboard/campaign'); setIsMobileMenuOpen(false); }}>
                    <div className="md-icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"></path>
                        <path d="M12 2v2"></path>
                        <path d="M12 20v2"></path>
                      </svg>
                    </div>
                    <span>Campaign</span>
                  </div>
                </nav>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Search Overlay */}
      {showSearch && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', justifyContent: 'center', paddingTop: 80 }}
          onClick={() => setShowSearch(false)}
        >
          <div
            style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, width: '100%', maxWidth: 560, height: 'fit-content', maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Search input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--ui-border)' }}>
              <Search size={18} color="var(--ui-text-muted)" />
              <input
                autoFocus
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setShowSearch(false)}
                style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--ui-text-primary)', fontSize: 15 }}
              />
              <button onClick={() => setShowSearch(false)} style={{ background: 'none', outline: '1px solid var(--ui-border)', color: 'var(--ui-text-muted)', cursor: 'pointer', fontSize: 12, padding: '2px 8px', borderRadius: 4 }}>ESC</button>
            </div>
            {/* Results */}
            <div style={{ overflowY: 'auto', padding: '8px 0' }}>
              {filteredSearch.length === 0 ? (
                <div style={{ padding: '20px 16px', color: 'var(--ui-text-muted)', fontSize: 13, textAlign: 'center' }}>No results found</div>
              ) : filteredSearch.map(item => (
                <div
                  key={item.path}
                  onClick={() => { navigate(item.path); setShowSearch(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = getComputedStyle(document.documentElement).getPropertyValue('--ui-bg-elevated').trim())}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <Search size={14} color="var(--ui-text-secondary)" />
                  <span style={{ color: 'var(--ui-text-primary)', fontSize: 14 }}>{item.label}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--ui-text-secondary)', fontSize: 11 }}>{item.path}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
